// =============================================================================
// app/src/lib/compraNaHora.ts — a compra paga NA HORA ao fornecedor (095)
//
// A tela "Paguei fornecedor" (paginas/CompraNaHora.tsx) é um formulário de
// balcão: um toque no fornecedor, o valor, um toque na forma, Lançar. Tudo o
// que ela decide sem banco mora aqui, para ser testado sem desenhar a tela:
//   * o que falta para o botão acender (validarCompraNaHora);
//   * o texto do botão e da confirmação ("Lançar R$ 350,00 em Pix");
//   * o pedido que vai à RPC (paraEnvio);
//   * o resumo do mês que só o DONO vê (resumoDoMes).
//
// Quem nega de verdade é o banco: registrar_compra_na_hora recusa forma,
// data e papel com as mesmas réguas (contas_a_pagar.sql). Repetir aqui é para
// o botão não acender e depois dar erro no meio do balcão.
// =============================================================================
import type { ContaPagar, FormaNaHora, Papel, PaymentMethod } from '@core/types';
import { brl, paraNumero } from './formato';

/** As duas que o balcão usa: botões grandes. */
export const FORMAS_PRINCIPAIS: ReadonlyArray<{ id: FormaNaHora; rotulo: string }> = [
  { id: 'dinheiro', rotulo: 'Dinheiro' },
  { id: 'pix', rotulo: 'Pix' },
];

/** As outras, atrás do link "outra forma". Boleto não está aqui de
 *  propósito: boleto a vencer é conta, e vai em Contas a pagar. */
export const FORMAS_OUTRAS: ReadonlyArray<{ id: FormaNaHora; rotulo: string }> = [
  { id: 'debito', rotulo: 'Débito' },
  { id: 'credito', rotulo: 'Crédito' },
  { id: 'transferencia', rotulo: 'Transferência' },
];

const ROTULOS: Record<string, string> = Object.fromEntries(
  [...FORMAS_PRINCIPAIS, ...FORMAS_OUTRAS].map((f) => [f.id, f.rotulo]),
);

export const rotuloDaForma = (f: PaymentMethod | null | undefined): string =>
  (f && ROTULOS[f]) || (f ?? '—');

/** Acima disto a tela pede uma segunda olhada antes de gravar: é o "35000"
 *  digitado no lugar de "350,00" — no caixa, um erro de 100 vezes. */
export const LIMITE_DA_CONFIRMACAO = 5000;

/** O gerente desfaz o que ELE lançou só nestes minutos (o banco confere). */
export const JANELA_DO_DESFAZER_MS = 15 * 60_000;

/** O começo da descrição que registrar_compra_na_hora grava. O MESMO texto do
 *  SQL — mudou lá, muda aqui (o nome do fornecedor é tirado dele). */
export const PREFIXO_DA_DESCRICAO = 'Compra na hora — ';

export interface CamposDaCompra {
  /** O fornecedor cadastrado escolhido no chip. Nulo = "Outro". */
  fornecedorId: string | null;
  /** O nome digitado no chip "Outro" — vale só sem fornecedorId. */
  nome: string;
  /** Como foi digitado ("350", "1.500,00"). Converte por paraNumero. */
  valor: string;
  forma: FormaNaHora | null;
  observacao: string;
  /** 'YYYY-MM-DD' — o dia da compra (o dia da loja). */
  data: string;
}

/**
 * O valor digitado, em reais com centavos — ou nulo se não é número.
 *
 * Sempre por paraNumero (regra 9 do CLAUDE.md): `Number('1.500')` é 1,5, e foi
 * assim que R$ 1.500,00 já virou R$ 1,50 no caixa.
 */
export function valorDaCompra(texto: string): number | null {
  const n = paraNumero(texto);
  if (n === null) return null;
  return Math.round(n * 100) / 100;
}

/**
 * O que falta para lançar, na ordem da tela — ou nulo quando dá para lançar.
 * A mensagem é a dica embaixo do botão: diz o PRÓXIMO toque, não um erro.
 */
export function validarCompraNaHora(
  c: CamposDaCompra, papel: Papel | null, hoje: string,
): string | null {
  if (!c.fornecedorId && !c.nome.trim()) {
    return 'Toque no fornecedor — ou em “Outro” e escreva o nome.';
  }
  const v = valorDaCompra(c.valor);
  if (v === null) return 'Digite quanto pagou.';
  if (v <= 0) return 'O valor precisa ser maior que zero.';
  if (!c.forma) return 'Toque em como pagou.';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(c.data)) return 'A data da compra não é válida.';
  if (c.data > hoje) return 'A data da compra não pode estar no futuro.';
  // A mesma régua do banco: dia passado é do dono. O gerente lança o de hoje.
  if (c.data !== hoje && papel !== 'dono') return 'Lançar compra de outro dia é só do dono.';
  return null;
}

/** Acima de R$ 5.000 a tela pergunta "Confere?" antes de gravar. */
export const precisaConfirmar = (valor: number | null): boolean =>
  valor !== null && valor > LIMITE_DA_CONFIRMACAO;

/** O texto do botão: diz o que vai acontecer, com o valor e a forma. */
export function rotuloDoBotao(c: Pick<CamposDaCompra, 'valor' | 'forma'>, gravando: boolean): string {
  if (gravando) return 'Lançando…';
  const v = valorDaCompra(c.valor);
  if (v === null || v <= 0 || !c.forma) return 'Lançar';
  return `Lançar ${brl(v)} em ${rotuloDaForma(c.forma)}`;
}

/** O pedido da RPC registrar_compra_na_hora. Data de hoje vai NULA: o banco
 *  usa o dia da LOJA, e o relógio do celular não decide nada. */
export interface EnvioDaCompra {
  valor: number;
  forma: FormaNaHora;
  fornecedorId: string | null;
  nome: string | null;
  observacao: string | null;
  data: string | null;
}

export function paraEnvio(c: CamposDaCompra, hoje: string): EnvioDaCompra {
  const valor = valorDaCompra(c.valor);
  if (valor === null || valor <= 0 || !c.forma) {
    // Quem chama já validou; chegar aqui é bug de tela, e gravar zero seria pior.
    throw new Error(validarCompraNaHora(c, 'dono', hoje) ?? 'Compra incompleta.');
  }
  return {
    valor,
    forma: c.forma,
    fornecedorId: c.fornecedorId,
    nome: c.fornecedorId ? null : c.nome.trim() || null,
    observacao: c.observacao.trim() || null,
    data: c.data === hoje ? null : c.data,
  };
}

/** A confirmação depois de gravar: só o que a pessoa DIGITOU — nenhum total
 *  (o gerente não vê o mês, decisão de 28/09/2026). */
export const resumoDoLancado = (valor: number, forma: PaymentMethod, nome: string): string =>
  `Lançado: ${brl(valor)} · ${rotuloDaForma(forma)} · ${nome}`;

/** O link "desfazer" da confirmação vale 15 minutos (o banco confere). */
export const dentroDaJanela = (lancadoEm: number, agora: number): boolean =>
  agora - lancadoEm < JANELA_DO_DESFAZER_MS;

// --- O que vem da Entrada de mercadoria ---------------------------------------
//
// A Entrada passa fornecedor, total e observação por ESTADO DO ROTEADOR, nunca
// por query string: valor de compra na URL fica no histórico do navegador e em
// log de servidor. Aqui se lê esse estado sem confiar no formato.

export interface DaEntrada {
  fornecedorId: string | null;
  /** Já no formato do campo ("350,00"); vazio quando não veio valor. */
  valor: string;
  observacao: string;
}

export function lerEstadoDaEntrada(estado: unknown): DaEntrada {
  const e = (estado && typeof estado === 'object' ? estado : {}) as Record<string, unknown>;
  const n = typeof e.valor === 'number' && Number.isFinite(e.valor) && e.valor > 0 ? e.valor : null;
  return {
    fornecedorId: typeof e.fornecedorId === 'string' && e.fornecedorId ? e.fornecedorId : null,
    valor: n === null ? '' : n.toFixed(2).replace('.', ','),
    observacao: typeof e.observacao === 'string' ? e.observacao : '',
  };
}

/**
 * O estado da Entrada é de UMA leitura: lido, sai do histórico.
 *
 * O react-router guarda o `state` no `history.state`, e ele sobrevive ao
 * lançamento — o onSuccess só limpa o estado do React. Sem isto, quem lança,
 * vai a outra tela e volta (o "voltar" do Android, ou recarregar a página)
 * encontra o formulário preenchido DE NOVO com o mesmo fornecedor, valor e
 * nota, sem o aviso "Lançado" — e um toque em Pix e Lançar grava a mesma
 * compra duas vezes. O banco não tem trava de repetição (revisão do #273).
 *
 * Troca a entrada do histórico (replace), sem estado. Sem estado, não faz nada:
 * a tela chama isto a cada mudança de endereço, e navegar à toa empilharia
 * redesenhos.
 */
export function limparEstadoDaEntrada(
  local: { pathname: string; search: string; hash: string; state: unknown },
  navegar: (para: string, opcoes: { replace: true; state: null }) => void,
): void {
  if (local.state === null || local.state === undefined) return;
  navegar(`${local.pathname}${local.search}${local.hash}`, { replace: true, state: null });
}

/**
 * O valor que a Entrada leva para o "Pagou agora?": o total da entrada só
 * quando TODA peça tem custo digitado. Com custo em parte das linhas, a soma
 * é de um pedaço — 5 peças com custo em 2 dariam o valor dessas 2 —, e o campo
 * "Quanto?" nasceria com um número que não foi o pago ao fornecedor, a um
 * toque de ser gravado. Nesse caso vai só o fornecedor e a nota; o valor a
 * pessoa digita (revisão do #273).
 */
export function valorParaOPagamento(
  itens: ReadonlyArray<{ quantidade: number; custo_unit: number }>,
): number | null {
  if (itens.length === 0) return null;
  const completo = itens.every((i) =>
    Number.isFinite(i.quantidade) && i.quantidade > 0
    && Number.isFinite(i.custo_unit) && i.custo_unit > 0);
  if (!completo) return null;
  const total = itens.reduce((s, i) => s + i.quantidade * i.custo_unit, 0);
  return Math.round(total * 100) / 100;
}

// --- O mês, só do dono ---------------------------------------------------------

/** As colunas da lista do mês. O `satisfies` trava contra core/types.ts. */
export const COLUNAS_DA_COMPRA = [
  'id', 'descricao', 'fornecedor_id', 'valor_real', 'forma_pagamento', 'pago_em',
  'pago_por_nome', 'observacao', 'created_at', 'criado_por', 'cancelada_em',
] as const satisfies ReadonlyArray<keyof ContaPagar>;

export type CompraNaHora = Pick<ContaPagar, (typeof COLUNAS_DA_COMPRA)[number]>;

/** O nome de quem vendeu, tirado da descrição congelada. */
export function nomeDaCompra(c: Pick<ContaPagar, 'descricao'>): string {
  return c.descricao.startsWith(PREFIXO_DA_DESCRICAO)
    ? c.descricao.slice(PREFIXO_DA_DESCRICAO.length).trim()
    : c.descricao;
}

/** "Balcão do lado" e "balcao do  lado" são o mesmo "Outro". */
const chaveDoNome = (nome: string): string =>
  nome.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();

export interface ResumoDoMes {
  total: number;
  quantas: number;
  porForma: Array<{ forma: PaymentMethod | null; valor: number; quantas: number }>;
  porFornecedor: Array<{ chave: string; nome: string; valor: number; quantas: number }>;
}

/**
 * O mês em três respostas: quanto, como e a quem. Do maior para o menor.
 *
 * Soma em CENTAVOS inteiros: somar 0,1 em ponto flutuante cem vezes não dá
 * 10,00, e o dono confere este número contra o extrato do banco.
 *
 * Fornecedor cadastrado agrupa pelo id, com o nome ATUAL do cadastro quando ele
 * vem em `nomes`; o "Outro" agrupa pelo nome digitado, sem acento nem caixa.
 */
export function resumoDoMes(
  linhas: readonly CompraNaHora[], nomes?: ReadonlyMap<string, string>,
): ResumoDoMes {
  const validas = linhas.filter((l) => !l.cancelada_em);
  const centavos = (l: CompraNaHora) => Math.round(Number(l.valor_real ?? 0) * 100);

  const formas = new Map<string, { forma: PaymentMethod | null; c: number; quantas: number }>();
  const fornecedores = new Map<string, { chave: string; nome: string; c: number; quantas: number }>();
  let total = 0;

  for (const l of validas) {
    const c = centavos(l);
    total += c;

    const kf = l.forma_pagamento ?? '';
    const f = formas.get(kf) ?? { forma: l.forma_pagamento, c: 0, quantas: 0 };
    f.c += c; f.quantas += 1;
    formas.set(kf, f);

    const nomeDigitado = nomeDaCompra(l);
    const chave = l.fornecedor_id ? `id:${l.fornecedor_id}` : `nome:${chaveDoNome(nomeDigitado)}`;
    const nome = (l.fornecedor_id && nomes?.get(l.fornecedor_id)) || nomeDigitado;
    const g = fornecedores.get(chave) ?? { chave, nome, c: 0, quantas: 0 };
    g.c += c; g.quantas += 1;
    fornecedores.set(chave, g);
  }

  const emReais = (c: number) => c / 100;
  return {
    total: emReais(total),
    quantas: validas.length,
    porForma: [...formas.values()]
      .sort((a, b) => b.c - a.c)
      .map((f) => ({ forma: f.forma, valor: emReais(f.c), quantas: f.quantas })),
    porFornecedor: [...fornecedores.values()]
      .sort((a, b) => b.c - a.c || a.nome.localeCompare(b.nome, 'pt-BR'))
      .map((g) => ({ chave: g.chave, nome: g.nome, valor: emReais(g.c), quantas: g.quantas })),
  };
}

/** O primeiro dia do mês seguinte ('2026-12' → '2027-01-01'): o fim
 *  exclusivo do recorte do mês, sem passar por Date (fuso). */
export function inicioDoMesSeguinte(mes: string): string {
  const ano = Number(mes.slice(0, 4));
  const m = Number(mes.slice(5, 7));
  return m === 12 ? `${ano + 1}-01-01` : `${ano}-${String(m + 1).padStart(2, '0')}-01`;
}
